/**
 * InventoryPanel - UI panel for managing inventory
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { InventoryComponent } from '../items/InventoryComponent';
import { ItemStack, ArmorItem } from '../items/Item';
import { t, Localization } from '../systems/Localization';
import { renderItemIcon, hasItemIcon } from './ItemIcons';

export class InventoryPanel extends UIPanel {
  private inventory: InventoryComponent | null = null;
  private hoveredSlot: number = -1;
  private slotSize: number = 50;
  private slotsPerRow: number = 5;
  private padding: number = 10;

  // Drag and drop state
  private draggingSlot: number = -1;
  private dragStartPos: Vector2 = new Vector2(0, 0);
  private currentMousePos: Vector2 = new Vector2(0, 0);
  private isDraggingItem: boolean = false;

  // Double-click detection
  private lastClickTime: number = 0;
  private lastClickSlot: number = -1;
  private doubleClickThreshold: number = 300; // ms

  // Callback for when item is dropped outside the panel
  public onItemDroppedOutside: ((item: ItemStack) => void) | null = null;

  // Callback for double-click equip
  public onEquipItem: ((item: ArmorItem, slotIndex: number) => void) | null = null;

  constructor() {
    super(0, 0, 300, 350);
    this.isDraggable = true;
  }

  show(): void {
    super.show();
    this.title = t('panel_inventory');
  }

  setInventory(inventory: InventoryComponent): void {
    this.inventory = inventory;
  }

  protected onPanelClick(pos: Vector2): boolean {
    if (!this.inventory) return true;

    // If we're dragging, handle drop
    if (this.isDraggingItem) {
      return true; // Let handleMouseUp handle it
    }

    const slot = this.getSlotAtPosition(pos);
    if (slot >= 0) {
      // Select slot for hotbar (only if not starting a drag)
      if (slot < 10 && !this.inventory.getSlot(slot)) {
        this.inventory.setSelectedSlot(slot);
      }
      return true;
    }

    return true;
  }

  /** Handle mouse down - start potential drag or detect double-click */
  handleMouseDown(pos: Vector2): boolean {
    if (!this.isVisible || !this.containsPoint(pos)) return false;

    // Check if clicking on title bar for panel dragging
    if (pos.y < this.bounds.y + this.titleBarHeight) {
      return super.handleMouseDown(pos);
    }

    if (!this.inventory) return false;

    const slot = this.getSlotAtPosition(pos);
    const now = Date.now();

    // Check for double-click on same slot
    if (slot >= 0 && slot === this.lastClickSlot && (now - this.lastClickTime) < this.doubleClickThreshold) {
      const itemStack = this.inventory.getSlot(slot);
      if (itemStack && itemStack.item instanceof ArmorItem && this.onEquipItem) {
        this.onEquipItem(itemStack.item, slot);
        this.lastClickTime = 0;
        this.lastClickSlot = -1;
        return true;
      }
    }

    // Update last click tracking
    this.lastClickTime = now;
    this.lastClickSlot = slot;

    // Handle item slot dragging
    if (slot >= 0 && this.inventory.getSlot(slot)) {
      this.draggingSlot = slot;
      this.dragStartPos = pos;
      this.currentMousePos = pos;
      this.isDraggingItem = false;
      return true;
    }
    return true; // Consume click inside panel
  }

  /** Handle mouse up - complete drag or click */
  handleMouseUp(pos: Vector2): boolean {
    // Let base class handle panel dragging
    if (super.handleMouseUp(pos)) {
      return true;
    }

    if (this.draggingSlot >= 0 && this.isDraggingItem && this.inventory) {
      const targetSlot = this.getSlotAtPosition(pos);

      if (targetSlot >= 0 && targetSlot !== this.draggingSlot) {
        // Dropped on another slot - swap items
        this.inventory.swapSlots(this.draggingSlot, targetSlot);
      } else if (targetSlot < 0 && !this.containsPoint(pos)) {
        // Dropped outside the panel - drop item on ground
        const draggedItem = this.inventory.getSlot(this.draggingSlot);
        if (draggedItem && this.onItemDroppedOutside) {
          // Create a copy of the item stack to drop
          const itemToDrop: ItemStack = {
            item: draggedItem.item,
            quantity: draggedItem.quantity
          };
          // Remove from inventory
          this.inventory.removeItemAtSlot(this.draggingSlot, draggedItem.quantity);
          // Notify callback to create dropped item
          this.onItemDroppedOutside(itemToDrop);
        }
      }

      this.draggingSlot = -1;
      this.isDraggingItem = false;
      return true;
    }

    // If we weren't dragging, it's a click - select hotbar slot
    if (this.draggingSlot >= 0 && !this.isDraggingItem) {
      const slot = this.draggingSlot;
      if (slot < 10) {
        this.inventory?.setSelectedSlot(slot);
      }
    }

    this.draggingSlot = -1;
    this.isDraggingItem = false;
    return false;
  }

  handleMouseMove(pos: Vector2): boolean {
    const panelDragging = super.handleMouseMove(pos);

    this.currentMousePos = pos;

    // Check if we should start dragging an item
    if (this.draggingSlot >= 0 && !this.isDraggingItem) {
      const dragThreshold = 5;
      const dist = Math.sqrt(
        Math.pow(pos.x - this.dragStartPos.x, 2) +
        Math.pow(pos.y - this.dragStartPos.y, 2)
      );
      if (dist > dragThreshold) {
        this.isDraggingItem = true;
      }
    }

    // Always update hovered slot for tooltip (even during panel drag)
    if (this.isVisible && this.containsPoint(pos)) {
      this.hoveredSlot = this.getSlotAtPosition(pos);
    } else {
      this.hoveredSlot = -1;
    }

    return panelDragging || this.isDraggingItem || this.containsPoint(pos);
  }

  /** Check if currently dragging an item */
  isItemDragging(): boolean {
    return this.isDraggingItem;
  }

  /** Get the slot being dragged */
  getDraggingSlot(): number {
    return this.draggingSlot;
  }

  private getSlotAtPosition(pos: Vector2): number {
    if (!this.inventory) return -1;

    const contentX = this.bounds.x + this.padding;
    const contentY = this.bounds.y + this.titleBarHeight + this.padding;

    const relX = pos.x - contentX;
    const relY = pos.y - contentY;

    if (relX < 0 || relY < 0) return -1;

    const col = Math.floor(relX / (this.slotSize + 5));
    const row = Math.floor(relY / (this.slotSize + 5));

    if (col >= this.slotsPerRow) return -1;

    const slot = row * this.slotsPerRow + col;
    if (slot >= this.inventory.getCapacity()) return -1;

    return slot;
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    if (!this.inventory) {
      ctx.fillStyle = '#888';
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('No inventory', this.bounds.x + this.bounds.width / 2, this.bounds.y + 100);
      return;
    }

    const startX = this.bounds.x + this.padding;
    const startY = this.bounds.y + this.titleBarHeight + this.padding;
    const hoverSlot = this.isDraggingItem ? this.getSlotAtPosition(this.currentMousePos) : -1;

    // Render slots
    const capacity = this.inventory.getCapacity();
    for (let i = 0; i < capacity; i++) {
      const col = i % this.slotsPerRow;
      const row = Math.floor(i / this.slotsPerRow);
      const x = startX + col * (this.slotSize + 5);
      const y = startY + row * (this.slotSize + 5);

      const isHovered = i === this.hoveredSlot;
      const isDropTarget = i === hoverSlot && this.isDraggingItem;
      const isBeingDragged = i === this.draggingSlot && this.isDraggingItem;
      const isHotbar = i < 10;
      const slot = this.inventory.getSlot(i);

      // Slot background
      if (isDropTarget) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.7)';
      } else if (isHovered && !this.isDraggingItem) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.3)';
      } else {
        ctx.fillStyle = 'rgba(50, 50, 70, 0.8)';
      }
      ctx.fillRect(x, y, this.slotSize, this.slotSize);

      // Border
      if (isDropTarget) {
        ctx.strokeStyle = '#7dffcc';
        ctx.lineWidth = 3;
      } else if (isHotbar) {
        ctx.strokeStyle = '#4ecca3';
        ctx.lineWidth = isHovered ? 2 : 1;
      } else {
        ctx.strokeStyle = '#555';
        ctx.lineWidth = isHovered ? 2 : 1;
      }
      ctx.strokeRect(x, y, this.slotSize, this.slotSize);

      // Item (dimmed if being dragged)
      if (slot && !isBeingDragged) {
        this.renderItemSlot(ctx, x, y, slot);
      } else if (slot && isBeingDragged) {
        ctx.globalAlpha = 0.3;
        this.renderItemSlot(ctx, x, y, slot);
        ctx.globalAlpha = 1.0;
      }

      // Hotbar number
      if (isHotbar) {
        ctx.fillStyle = '#666';
        ctx.font = '10px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`${(i + 1) % 10}`, x + 2, y + 10);
      }
    }

    // Render dragged item at cursor
    if (this.isDraggingItem && this.draggingSlot >= 0) {
      const draggedItem = this.inventory.getSlot(this.draggingSlot);
      if (draggedItem) {
        const dragX = this.currentMousePos.x - this.slotSize / 2;
        const dragY = this.currentMousePos.y - this.slotSize / 2;

        // Shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(dragX + 4, dragY + 4, this.slotSize, this.slotSize);

        // Item background
        ctx.fillStyle = 'rgba(30, 30, 50, 0.95)';
        ctx.fillRect(dragX, dragY, this.slotSize, this.slotSize);
        ctx.strokeStyle = '#4ecca3';
        ctx.lineWidth = 2;
        ctx.strokeRect(dragX, dragY, this.slotSize, this.slotSize);

        this.renderItemSlot(ctx, dragX, dragY, draggedItem);
      }
    }

    // Render tooltip for hovered item (when not dragging)
    if (!this.isDraggingItem && this.hoveredSlot >= 0) {
      const slot = this.inventory.getSlot(this.hoveredSlot);
      if (slot) {
        this.renderTooltip(ctx, slot);
      }
    }

    // Capacity info
    ctx.fillStyle = '#888';
    ctx.font = '12px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(
      `${this.inventory.getUsedSlots()}/${capacity}`,
      this.bounds.x + this.bounds.width - 10,
      this.bounds.y + this.bounds.height - 10
    );
  }

  private renderItemSlot(ctx: CanvasRenderingContext2D, x: number, y: number, slot: ItemStack): void {
    const iconSize = this.slotSize - 10;
    const iconX = x + 5;
    const iconY = y + 5;

    // Try to render pixel art icon first
    if (hasItemIcon(slot.item.id)) {
      renderItemIcon(ctx, slot.item.id, iconX, iconY, iconSize);
    } else {
      // Fallback to colored square with letter
      ctx.fillStyle = slot.item.getColor();
      ctx.fillRect(iconX, iconY, iconSize, iconSize);

      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(iconX, iconY, iconSize, iconSize);

      // Item initial
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(slot.item.name.charAt(0), x + this.slotSize / 2, y + this.slotSize / 2 + 6);
    }

    // Quantity with background for better visibility
    if (slot.quantity > 1) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(x + this.slotSize - 20, y + this.slotSize - 16, 18, 14);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${slot.quantity}`, x + this.slotSize - 3, y + this.slotSize - 3);
    }
  }

  private renderTooltip(ctx: CanvasRenderingContext2D, slot: ItemStack): void {
    // Render tooltip to the LEFT of inventory panel (equipment panel is on the right)
    const tooltipWidth = 180;
    const tooltipHeight = 80;
    const tooltipX = this.bounds.x - tooltipWidth - 10;
    const tooltipY = this.bounds.y + this.titleBarHeight;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    ctx.strokeStyle = '#4ecca3';
    ctx.lineWidth = 1;
    ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Get localized item info
    const itemInfo = Localization.getItem(slot.item.id);
    const itemName = itemInfo?.name || slot.item.name;
    const itemDesc = itemInfo?.description || slot.item.description;

    // Item name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(itemName, tooltipX + 10, tooltipY + 20);

    // Item type
    ctx.fillStyle = slot.item.getColor();
    ctx.font = '11px monospace';
    ctx.fillText(slot.item.type, tooltipX + 10, tooltipY + 35);

    // Description
    ctx.fillStyle = '#aaa';
    ctx.font = '11px monospace';
    const maxChars = 25;
    if (itemDesc.length > maxChars) {
      ctx.fillText(itemDesc.substring(0, maxChars) + '...', tooltipX + 10, tooltipY + 55);
    } else {
      ctx.fillText(itemDesc, tooltipX + 10, tooltipY + 55);
    }

    // Value
    ctx.fillStyle = '#f1c40f';
    ctx.fillText(`${t('tooltip_value')}: ${slot.item.value}`, tooltipX + 10, tooltipY + 70);
  }
}
